import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, MessageSquare, Sparkles, Loader2, Megaphone } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const endRef = useRef(null);

  const loadThreads = () => api.get("/admin/threads").then((r) => setThreads(r.data)).catch(() => {});
  const loadThread = (uid) => api.get(`/admin/messages/${uid}`).then((r) => setMsgs(r.data)).catch(() => {});

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (!active) return;
    loadThread(active.user_id);
    const t = setInterval(() => loadThread(active.user_id), 20000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const openThread = (t) => { setActive(t); setText(""); };

  const broadcast = async () => {
    if (!window.confirm("Inviare il riepilogo mensile in chat a TUTTI i segnalatori?")) return;
    setBroadcasting(true);
    try {
      const { data } = await api.post("/admin/messages/broadcast-summary");
      toast.success(`Riepilogo inviato a ${data.sent} segnalatori.`);
      loadThreads();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBroadcasting(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/messages/${active.user_id}`, { body: text.trim() });
      setText("");
      loadThread(active.user_id);
      loadThreads();
      toast.success("Messaggio inviato.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const genDraft = async () => {
    if (!active) return;
    setDrafting(true);
    try {
      const { data } = await api.get(`/admin/messages/${active.user_id}/summary-draft`);
      setText(data.draft);
      toast.success("Riepilogo generato: puoi modificarlo prima di inviare.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setDrafting(false); }
  };

  return (
    <DashboardLayout title="Messaggi" admin>
      <div className="grid lg:grid-cols-3 gap-4 h-[calc(100vh-9rem)]">
        {/* threads */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-auto">
          <div className="px-4 py-3 border-b border-slate-100 text-xs font-semibold uppercase text-slate-400">Segnalatori</div>
          {threads.length === 0 && <p className="p-4 text-sm text-slate-400">Nessun segnalatore.</p>}
          {threads.map((t) => (
            <button key={t.user_id} onClick={() => openThread(t)} data-testid={`thread-${t.user_id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${active?.user_id === t.user_id ? "bg-slate-50" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-800 truncate">{t.name}</span>
                {t.unread > 0 && <span className="ml-2 h-5 min-w-5 px-1.5 rounded-full bg-[#D4AF37] text-[11px] font-bold text-[#0A1128] flex items-center justify-center">{t.unread}</span>}
              </div>
              <p className="text-xs text-slate-400 truncate">{t.last_message || "Nessun messaggio"}</p>
            </button>
          ))}
        </div>

        {/* thread */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
              <MessageSquare className="h-10 w-10" />
              <p className="mt-3 text-sm">Seleziona un segnalatore per iniziare a chattare.</p>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{active.name}</p>
                  <p className="text-xs text-slate-400">{active.email}</p>
                </div>
                <button onClick={genDraft} disabled={drafting} data-testid="gen-summary-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37] px-4 py-2 text-sm font-medium text-[#8a6d0f] hover:bg-[#D4AF37]/10">
                  {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Genera riepilogo mensile
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-3" data-testid="admin-messages-thread">
                {msgs.map((m) => {
                  const team = m.sender_role === "team";
                  return (
                    <div key={m.id} className={`flex ${team ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${team ? "bg-[#0A1128] text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                        {!team && <p className="text-[11px] font-semibold text-slate-500 mb-1">{m.sender_name}</p>}
                        {m.body}
                        <p className={`text-[10px] mt-1 ${team ? "text-white/50" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString("it-IT")}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-slate-100 flex items-end gap-2">
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} data-testid="admin-message-input"
                  placeholder="Scrivi al segnalatore…"
                  className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none resize-none" />
                <button disabled={sending} data-testid="admin-message-send-btn" className="btn-gold rounded-full h-11 w-11 flex items-center justify-center shrink-0">
                  <Send className="h-5 w-5" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
